<script setup>
import { computed, ref } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import { translator } from "@shop/frontend/web/js/services/TranslatorService";
import AdditionalService from "@vueApp/views/history/components/AdditionalService.vue";
import AdditionalServiceInfo from "@vueApp/views/history/components/AdditionalServiceInfo.vue";
import api from "@shop/frontend/web/js/api";

translator.getLanguages();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  inactive: {
    type: Boolean,
    default: false,
  },
  short: {
    type: Boolean,
    default: false,
  },
  allowedLoyalty: {
    type: Array,
    default: null,
  },
  itemsCase: {
    type: Object,
    default: {},
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  additionalTickets: {
    type: Array,
    default: [],
  },
  itemOrderId: {
    type: Number,
    default: null,
  },
  itemParentId: {
    type: [Number, String],
    default: "",
  },
  type: {
    type: String,
    default: "",
  },
});

// Emits
const emits = defineEmits(["removeTicket", "changeLoyalty"]);

// Data
const isAdditionalServicesModalShow = ref(false);
const refundData = ref(null);
const currentOrderForRefund = ref(null);
const isAdditionalServiceInfoModalShow = ref(false);
const loyaltyBtn = ref(null);
const isShowDropDownLoyalty = ref(false);
const additionalServiceInfo = ref({});
const additionalTicketsRef = ref(props.ticket.additionalTickets);
const currentAdditionalServicesInfo = ref({});

// Computed
const allowedLoyalty = computed(() => {
  if (props.allowedLoyalty === null) return null;
  return [{ id: 0, categoryId: 0, name: "Основной" }, ...props.allowedLoyalty];
});
const currentLoyalty = computed(() => {
  if (props.ticket.loyaltyCategoryId === null) return { id: 0, categoryId: 0, name: 'Основной' };
  return allowedLoyalty.value.find((item) => item.id === props.ticket.loyaltyCategoryId);
});

const sectorName = computed(() =>
  props.ticket.sector.includes("Сектор") ? props.ticket.sector.replace(/Сектор/g, translator.t('main', 'Sector')) : `${translator.t('main', 'Sector')} ${props.ticket.sector}`
);

const totalAdditionalTicketsPrice = computed(() => {
  if (
    !props.ticket.additionalTickets ||
    !Array.isArray(props.ticket.additionalTickets) ||
    props.ticket.additionalTickets.length === 0
  ) {
    return 0;
  }
  return props.ticket.additionalTickets.reduce((sum, ticket) => sum + ticket.price, 0);
});

// Functions

function onAdditionalServices() {
  isAdditionalServicesModalShow.value = !isAdditionalServicesModalShow.value;
}

function onAdditionalServiceInfo({ extraInfo, uid }) {
  isAdditionalServiceInfoModalShow.value = !isAdditionalServiceInfoModalShow.value;
  currentAdditionalServicesInfo.value = {
    extraInfo,
    uid,
  };
}

function toggleDropDownLoyalty() {
  if (props.isHistory) {
    return;
  }

  isShowDropDownLoyalty.value = !isShowDropDownLoyalty.value;
}

function clickOutsideDropdown(e) {
  if (e.target === loyaltyBtn.value || e.target.parentElement === loyaltyBtn.value) {
    return;
  }

  isShowDropDownLoyalty.value = false;
}

function changeLoyalty(categoryLoyalty) {
  emits("changeLoyalty", {
    ticket: props.ticket,
    categoryId: categoryLoyalty.id,
    priceId: categoryLoyalty.categoryId ?? categoryLoyalty.priceId,
  });

  isShowDropDownLoyalty.value = false;
}

async function sendAdditionalServiceInfo(name, uid) {
  try {
    additionalServiceInfo.value[uid] = name;
    const res = await api.additionalServices.addAdditionalServicesInfo(name, uid);
  } catch (e) {
    console.log(e);
  }
}

function changeAdditionalTickets(value) {
  additionalTicketsRef.value = value;
}

</script>

<template>
  <div class="i-ticket" :class="{ inactive, short }" data-test-id="ticket">
    <div class="i-ticket__content">
      <div class="i-ticket__content-top">
        <div v-if="ticket.sector" class="i-ticket__sector">{{ sectorName }}</div>
        <div class="i-ticket__place">
          <span v-if="ticket.place === 'Входной билет'">
            {{ translator.t('main', 'Entrance ticket') }} 
          </span>
          <span v-else-if="!ticket.row && !ticket.col">
            {{ ticket.type }} 
          </span>
          <span v-else>
            {{ translator.t('main', 'Row') }} 
            {{ ticket.row }}, 
            {{ translator.t('main', 'Place') }} 
            {{ ticket.col }}
          </span>
        </div>
      </div>

      <div
        v-if="additionalTicketsRef?.length"
        @click="onAdditionalServices()"
        class="tickets__service js-ticket-service additional-services__added-service"
      >
        <svg width="17" height="17" aria-hidden="true" focusable="false">
          <use xlink:href="#service-added"></use>
        </svg>
        <span>
          {{
            additionalTicketsRef.length > 1
              ? translator.t("main", "Services added")
              : translator.t("main", "Service added")
          }}
        </span>
      </div>

      <div
        v-else-if="
          props.ticket?.additionalServices?.length > 0 && props.ticket.additionalTickets.length == 0
        "
        @click="addService"
        class="tickets__service js-ticket-service"
      >
        <button
          @click="onAdditionalServices()"
          class="btn-class js-additional-services additional-services__add-service"
        >
          <svg width="16" height="16" aria-hidden="true" focusable="false">
            <use xlink:href="#service-add"></use>
          </svg>
          <span>
            {{ translator.t("main", "Add a service") }}
          </span>
        </button>
      </div>

      <template v-if="ticket.additionalServices?.length > 0">
        <AdditionalService
        v-model="isAdditionalServicesModalShow"
        :refund-data="refundData"
        :chosen-order="currentOrderForRefund"
        :additional-services="props.ticket.additionalServices"
        :additional-tickets="props.ticket.additionalTickets"
        :item-order-id="itemOrderId"
        :item-parent-id="itemParentId"
        :item-uid="ticket.uid"
        @success-refund="onAdditionalServices"
        @update-carts="changeAdditionalTickets"
      />
      </template>
      

      <div class="i-ticket__bottom">
        <div class="i-ticket__loyalty-wrapper">
          <div
            v-if="props.allowedLoyalty?.length"
            class="dropdown"
            ref="loyaltyBtn"
            :disabled="props.inactive"
            :class="{ active: isShowDropDownLoyalty, dropdown_inactive: isHistory }"
            @click="toggleDropDownLoyalty"
          >
            <div class="dropdown__title">{{ currentLoyalty.name }}</div>
            <svg
              v-if="!props.inactive && !isHistory"
              class="dropdown__icon-down"
              height="16"
              width="16"
            >
              <use xlink:href="#down"></use>
            </svg>
          </div>
          <div
            v-if="isShowDropDownLoyalty"
            v-on-click-outside="clickOutsideDropdown"
            class="dropdown__list"
          >
            <div
              v-for="categoryLoyalty in allowedLoyalty"
              :key="categoryLoyalty.id"
              class="dropdown__item"
              @click="changeLoyalty(categoryLoyalty)"
            >
              <input v-model="currentLoyalty.id" type="radio" :value="categoryLoyalty.id" />
              <svg height="18" width="18">
                <use v-if="categoryLoyalty.id === 0" xlink:href="#main-preferential-category"></use>
                <use
                  v-else-if="categoryLoyalty.id === 1"
                  xlink:href="#child-preferential-category"
                ></use>
                <use v-else xlink:href="#other-preferential-category"></use>
              </svg>
              {{ categoryLoyalty.name }}
            </div>
          </div>
        </div>
        <div class="i-ticket__bottom-wrapper">
          <div class="i-ticket__place-price">
            {{ ticket.price + totalAdditionalTicketsPrice }} ₽
          </div>
          <div v-if="isHistory && ticket.isRefunded" class="i-ticket__refunded-message">
            {{ translator.t("main", "Refunded") }}
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="!isHistory"
      class="i-ticket__remove-button reset-btn"
      type="button"
      title="Удалить"
      @click="$emit('removeTicket', props.ticket)"
    >
      <svg height="15" width="15">
        <use xlink:href="#close-small"></use>
      </svg>
    </button>
  </div>
  <slot>
    <div></div>
  </slot>

  <div v-for="additionalService in additionalTicketsRef" :key="additionalService.uid">
    <div
      v-if="
        props.ticket.additionalServices.length > 0 &&
        additionalTicketsRef.length > 0 &&
        additionalService.isExtraInfoEnabled
      "
      class="tickets__service js-ticket-service"
    >
      <div v-if="additionalService.extraInfo || additionalServiceInfo[additionalService.uid]">
        <div class="additional-services__change-info-container">
          <span class="additional-services__info">{{
            additionalServiceInfo[additionalService.uid] || additionalService.extraInfo || ""
          }}</span>
          <span
            v-if="!props.isHistory"
            @click="onAdditionalServiceInfo(additionalService)"
            class="additional-services__change-info"
          >
            {{ translator.t("main", "change") }}</span
          >
        </div>
      </div>
      <div v-else>
        <button
          v-if="!props.isHistory"
          @click="onAdditionalServiceInfo(additionalService)"
          class="btn-class js-additional-services additional-services__add-info"
        >
          <svg width="16" height="16" aria-hidden="true" focusable="false">
            <use xlink:href="#info-service-add"></use>
          </svg>
          <span>
            {{ translator.t("main", "Add data") }} {{ additionalService.name }}
            {{ additionalService?.extraInfo || additionalServiceInfo[additionalService.uid] || "" }}
          </span>
        </button>
      </div>
    </div>
  </div>

  <AdditionalServiceInfo
    v-model="isAdditionalServiceInfoModalShow"
    @success-refund="onAdditionalServiceInfo"
    @send-info="sendAdditionalServiceInfo"
    :additional-service-item-uid="currentAdditionalServicesInfo.uid"
    :additional-service-item-info="currentAdditionalServicesInfo.name"
  />
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-ticket.scss";
</style>
